import React, { useEffect, Suspense, useRef } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Toaster } from '@/components/ui/toaster';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { CartProvider, useCart } from '@/contexts/CartContext';
import DevErrorBoundary from '@/components/DevErrorBoundary';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppWidget from '@/components/WhatsAppWidget';
import Home from '@/pages/Home';
import Catalog from '@/pages/Catalog';
const ProductConfigurator = React.lazy(() => import('@/pages/ProductConfigurator'));
import Cart from '@/pages/Cart';
import CheckoutResult from '@/pages/CheckoutResult';
import ThankYou from '@/pages/ThankYou';
import ThankYouCC from '@/pages/ThankYouCC';
// Backend removed: iCount thank-you route may be unused but can remain if purely client-side
import Admin from '@/pages/Admin';
import About from '@/pages/About';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import Returns from '@/pages/Returns';
import FAQ from '@/pages/FAQ';
import PrintQuality from '@/pages/PrintQuality';
import GarmentsQuality from '@/pages/GarmentsQuality';
import ServiceQuality from '@/pages/ServiceQuality';
import DevComposer from '@/pages/DevComposer';
import MethodsOfBranding from '@/pages/MethodsOfBranding';
import AccessibilityStatement from '@/pages/AccessibilityStatement';
import Works from '@/pages/Works';
import Contact from '@/pages/Contact';
import HeadwearLandingPage from '@/pages/HeadwearLandingPage';
import BuyerLandingPage from '@/pages/BuyerLandingPage';
import WinterCampaign from '@/pages/WinterCampaign';
import OpenAIAdsTracker from '@/components/OpenAIAdsTracker';
import NotFound from '@/pages/NotFound';
import {
  DEFAULT_SOCIAL_IMAGE_PATH,
  SITE_NAME,
  canonicalUrl,
  getSeoRoute,
  isKnownProductSku,
  buyerSeoRoutes,
  noindexRoutes,
  notFoundRoute,
  staticIndexableRoutes,
} from '@/seo/siteRoutes';
import {
  buildGoogleAdsSnapshot,
  readStoredGoogleAds,
  writeStoredGoogleAds,
  readGoogleClickFromCookies,
  detectDeviceCategory,
  mergeGoogleAdsUrlTracking,
} from '@/lib/googleAdsTracking';
import { captureWhatsAppSearchSitelinkEntry } from '@/lib/whatsappSearchEntry';

function WhatsAppSearchEntryCapture() {
  useEffect(() => {
    const capture = () => captureWhatsAppSearchSitelinkEntry();
    capture();
    if (document.prerendering !== true) return undefined;
    document.addEventListener('prerenderingchange', capture, { once: true });
    return () => document.removeEventListener('prerenderingchange', capture);
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname, search]);
  return null;
}

function RouteSeoDefaults() {
  const { pathname } = useLocation();
  const configuredRoute = getSeoRoute(pathname);
  const route = configuredRoute || notFoundRoute;
  const routeCanonical = configuredRoute ? canonicalUrl(route.path) : null;
  const socialImage = canonicalUrl(DEFAULT_SOCIAL_IMAGE_PATH);

  return (
    <Helmet htmlAttributes={{ lang: 'he', dir: 'rtl' }}>
      <title>{route.title}</title>
      <meta name="description" content={route.description} />
      <meta name="robots" content={route.robots} />
      {routeCanonical && <link rel="canonical" href={routeCanonical} />}
      <meta property="og:locale" content="he_IL" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={route.type === 'product' ? 'product' : 'website'} />
      <meta property="og:title" content={route.title} />
      <meta property="og:description" content={route.description} />
      {routeCanonical && <meta property="og:url" content={routeCanonical} />}
      <meta property="og:image" content={socialImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={route.title} />
      <meta name="twitter:description" content={route.description} />
      <meta name="twitter:image" content={socialImage} />
    </Helmet>
  );
}

function ProductRoute() {
  const { sku } = useParams();
  if (!isKnownProductSku(sku)) return <NotFound />;

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">{/* loading */}</div>}>
      <ProductConfigurator />
    </Suspense>
  );
}

function HeadwearRoute() {
  const { pathname } = useLocation();
  const route = getSeoRoute(pathname);
  if (route?.type !== 'headwear') return <NotFound />;
  return <HeadwearLandingPage />;
}

const STATIC_PAGE_COMPONENTS = Object.freeze({
  home: Home,
  catalog: Catalog,
  'winter-campaign': WinterCampaign,
  faq: FAQ,
  'print-quality': PrintQuality,
  'garments-quality': GarmentsQuality,
  'service-quality': ServiceQuality,
  about: About,
  terms: Terms,
  privacy: Privacy,
  returns: Returns,
  'methods-of-branding': MethodsOfBranding,
  accessibility: AccessibilityStatement,
  works: Works,
  contact: Contact,
});

const getNoindexRouteElement = (routeId) => {
  switch (routeId) {
    case 'cart':
      return <Cart />;
    case 'checkout-success':
      return <CheckoutResult success={true} />;
    case 'checkout-cancel':
      return <CheckoutResult success={false} />;
    case 'thank-you':
      return <ThankYou />;
    case 'thank-you-cc':
      return <ThankYouCC />;
    case 'admin':
      return <Admin />;
    default:
      throw new Error(`Missing React route implementation for SEO route "${routeId}"`);
  }
};

// Send Google Ads and GA4 page_view on SPA route changes
function AdsRouteTracker() {
  const { pathname, search } = useLocation();
  const initialRenderRef = useRef(true);
  useEffect(() => {
    try {
      const path = `${pathname}${search || ''}`;
      if (typeof window === 'undefined') return;
      const KEY = '__aw_last_path';

      if (initialRenderRef.current) {
        initialRenderRef.current = false;
        window[KEY] = path;
        return;
      }

      if (typeof window.gtag !== 'function') return;

      const last = window[KEY];
      if (last === path) return;

      const pageLocation = (() => {
        try {
          return window.location?.href || path;
        } catch {
          return path;
        }
      })();
      const basePayload = { page_location: pageLocation, page_path: path };

      window.gtag('event', 'page_view', { ...basePayload, send_to: 'AW-17646508237' });
      window.gtag('event', 'page_view', { ...basePayload, send_to: 'G-43KTYPJPNM' });
      window[KEY] = path;
    } catch {}
  }, [pathname, search]);
  return null;
}

// helper functions moved to lib/googleAdsTracking.js

function TrackingSync() {
  const { payload, mergePayload } = useCart();
  const location = useLocation();
  const hydratedRef = useRef(false);

  // On first mount, attempt to hydrate tracking data from storage or cookies.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const currentAds = payload?.tracking?.googleAds || {};
    const currentMetadata = payload?.tracking?.metadata || {};
    const stored = readStoredGoogleAds();
    const cookieClick = readGoogleClickFromCookies();
    const nextAds = buildGoogleAdsSnapshot(stored, currentAds, cookieClick);
    const nextMetadata = { ...currentMetadata };
    const trackedFields = [
      'gclid',
      'gbraid',
      'wbraid',
      'googleClickIdConflict',
      'campaign',
      'keyword',
      'utmSource',
      'utmMedium',
      'source',
      'lastCapturedAt',
      'clickCaptureVersion',
      'clickCaptureSource',
    ];
    const changed = trackedFields.some((key) => nextAds[key] !== currentAds[key]);
    let metadataChanged = false;

    const ensureConversionTime = (timestamp) => {
      if (!timestamp) return;
      if (nextMetadata.conversionTime) return;
      if (nextMetadata.conversionTime === timestamp) return;
      nextMetadata.conversionTime = timestamp;
      metadataChanged = true;
    };
    ensureConversionTime(nextAds.lastCapturedAt);

    if (!changed && !metadataChanged) return;

    try {
      mergePayload({
        tracking: {
          ...(payload?.tracking || {}),
          googleAds: nextAds,
          metadata: {
            ...nextMetadata,
          },
        },
      });
    } catch (e) {
      try { console.warn('[TrackingSync] mergePayload failed on hydrate', e?.message || String(e)); } catch (_) {}
    }

    writeStoredGoogleAds(nextAds, { conversionTime: nextMetadata.conversionTime });
  }, [mergePayload, payload?.tracking]);

  // On every route change, capture tracking params from the URL.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const search = location?.search || window.location.search || '';
    if (!search) return;
    const currentAds = payload?.tracking?.googleAds || {};
    const currentMetadata = payload?.tracking?.metadata || {};
    const nextMetadata = { ...currentMetadata };
    const capturedAt = new Date().toISOString();
    let metadataChanged = false;

    const ensureConversionTime = (timestamp, overwrite = false) => {
      if (!timestamp) return;
      if (!overwrite && nextMetadata.conversionTime) return;
      if (nextMetadata.conversionTime === timestamp) return;
      nextMetadata.conversionTime = timestamp;
      metadataChanged = true;
    };

    const {
      googleAds: nextAds,
      changed,
      clickCaptured,
    } = mergeGoogleAdsUrlTracking(currentAds, search, capturedAt);
    if (clickCaptured) ensureConversionTime(capturedAt, true);

    if (!changed && !metadataChanged) return;

    try {
      mergePayload({
        tracking: {
          ...(payload?.tracking || {}),
          googleAds: nextAds,
          metadata: {
            ...nextMetadata,
          },
        },
      });
    } catch (e) {
      try { console.warn('[TrackingSync] mergePayload failed on route change', e?.message || String(e)); } catch (_) {}
    }

    writeStoredGoogleAds(nextAds, { conversionTime: nextMetadata.conversionTime });
  }, [
    location?.search,
    mergePayload,
    payload?.tracking?.googleAds?.gclid,
    payload?.tracking?.googleAds?.gbraid,
    payload?.tracking?.googleAds?.wbraid,
    payload?.tracking?.googleAds?.googleClickIdConflict,
    payload?.tracking?.googleAds?.campaign,
    payload?.tracking?.googleAds?.keyword,
    payload?.tracking?.googleAds?.utmSource,
    payload?.tracking?.googleAds?.utmMedium,
    payload?.tracking?.googleAds?.clickCaptureVersion,
    payload?.tracking?.googleAds?.clickCaptureSource,
  ]);

  useEffect(() => {
    const device = detectDeviceCategory();
    if (!device) return;
    const currentDevice = payload?.tracking?.metadata?.device || '';
    if (currentDevice === device) return;
    try {
      mergePayload({
        tracking: {
          ...(payload?.tracking || {}),
          metadata: {
            ...(payload?.tracking?.metadata || {}),
            device,
          },
        },
      });
    } catch (e) {
      try { console.warn('[TrackingSync] mergePayload failed on device detection', e?.message || String(e)); } catch (_) {}
    }
  }, [mergePayload, payload?.tracking?.metadata?.device]);

  return null;
}

function AppFrame() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <RouteSeoDefaults />
      <Header />
      <ScrollToTop />
      <AdsRouteTracker />
      <main className="min-h-screen">
        <Routes>
          {staticIndexableRoutes.map((route) => {
            const PageComponent = STATIC_PAGE_COMPONENTS[route.id];
            if (!PageComponent) {
              throw new Error(`Missing React page component for SEO route "${route.id}"`);
            }
            return <Route key={route.id} path={route.path} element={<PageComponent />} />;
          })}
          <Route path="/headwear/:landingKey" element={<HeadwearRoute />} />
          {buyerSeoRoutes.map((route) => (
            <Route key={route.id} path={route.path} element={<BuyerLandingPage landingKey={route.landingKey} />} />
          ))}
          <Route path="/product/:sku" element={<ProductRoute />} />
          <Route path="/accessebility" element={<Navigate replace to="/accessibility" />} />
          {noindexRoutes.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              element={getNoindexRouteElement(route.id)}
            />
          ))}
          {process.env.NODE_ENV !== 'production' && (
            <Route path="/dev/composer" element={<DevComposer />} />
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <CartProvider>
        <DevErrorBoundary>
          <Router>
            <WhatsAppSearchEntryCapture />
            <TrackingSync />
            <OpenAIAdsTracker />
            <AppFrame />
          </Router>
        </DevErrorBoundary>
      </CartProvider>
    </LanguageProvider>
  );
}

export default App;
